import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import AccountLayout from "../AccountLayout";
import useProfile from "api/UserResource/useProfile";
import useGetUserOrders from "api/OrderResource/useGetUserOrders";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { getActiveCurrencyCodeLabel, getPaymentMethodLabel } from "utils";
import { Tag } from "primereact/tag";
import MoreOrderDetails from "./MoreOrderDetails";
import useUserOrdersFilters from "./useUserOrdersFilters";
import SearchInput from "./SearchInput";
import RowsPerPageSelector from "./RowsPerPageSelector";
import ClearFiltersButton from "./ClearFiltersButton";
import { CustomPaginator } from "components";

// Table cell templates
const ReferenceCell = ({ ecocash_reference, reference }) =>
	ecocash_reference || reference || "N/A";

const PaymentMethodCell = ({ payment_method, phone }) => {
	if (!payment_method?.startsWith("Ecocash")) {
		return <span>{payment_method}</span>;
	}

	return (
		<div className="flex flex-col">
			<span>{getPaymentMethodLabel(payment_method)}</span>
			{phone && <span className="text-xs font-bold">{phone}</span>}
		</div>
	);
};

const AmountCell = ({ total_amount, currency }) => (
	<div className="font-bold">
		{`${getActiveCurrencyCodeLabel(currency)} $${total_amount}`}
	</div>
);

const StatusCell = ({ status }) => (
	<Tag
		rounded
		className="font-sans text-xs"
		severity={status.toLowerCase() === "completed" ? "warn" : "danger"}
		value={status}
	/>
);

// Table header component
const TableHeader = ({
	totalRecords,
	filters,
	onSearch,
	onClear,
	onRowsPerPageChange,
}) => {
	const hasActiveFilters =
		filters.search || filters.page > 1 || filters.rowsPerPage !== 10;

	return (
		<div className="space-y-4">
			<h4 className="flex items-center justify-between gap-3 rounded-full max-w-max">
				<span>My orders</span>
				<span className="block bg-gray-400 rounded-full text-white py-1 w-14 text-center">
					{totalRecords}
				</span>
			</h4>

			<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
				<div className="flex items-center gap-4 ">
					<SearchInput
						onSearch={onSearch}
						onClear={onClear}
						currentSearch={filters.search}
						placeholder="Search order number, reference, etc..."
					/>
					<ClearFiltersButton
						onClear={onClear}
						hasActiveFilters={hasActiveFilters}
					/>
				</div>
				<RowsPerPageSelector
					value={filters.rowsPerPage}
					onChange={onRowsPerPageChange}
				/>
			</div>
		</div>
	);
};

const MyOrders = () => {
	const { data: profile, isLoading: isLoadingProfile } = useProfile();
	const { filters, updateFilters, clearSearch } = useUserOrdersFilters();

	const { data, isLoading: isLoadingOrders } = useGetUserOrders({
		queryParams: {
			page: filters.page,
			rows_per_page: filters.rowsPerPage,
			search: filters.search,
		},
	});

	const orders = data?.records;
	const totalRecords = data?.total_records || 0;
	const isLoading = isLoadingProfile || isLoadingOrders;

	const handleSearch = (searchTerm) => {
		updateFilters({ search: searchTerm, page: 1 });
	};

	const handleRowsPerPageChange = (rowsPerPage) => {
		updateFilters({ rowsPerPage, page: 1 });
	};

	const handlePageChange = (page) => {
		updateFilters({ page });
	};

	const handleFirstPageChange = (firstPage) => {
		const page = Math.floor(firstPage / filters.rowsPerPage) + 1;
		updateFilters({ page });
	};

	// console.log({ orders });

	return (
		<Container>
			<div className="mt-10 flex-1">
				<PaddingX>
					<AccountLayout
						isLoading={isLoading}
						profile={profile?.data}
						showProfile={false}
					>
						<div className="space-y-4">
							<DataTable
								loading={isLoadingOrders}
								value={orders}
								dataKey="id"
								header={
									<TableHeader
										totalRecords={totalRecords}
										filters={filters}
										onSearch={handleSearch}
										onClear={clearSearch}
										onRowsPerPageChange={handleRowsPerPageChange}
									/>
								}
								tableClassName="font-sans text-sm w-full"
								autoLayout
							>
								<Column
									body={(row) => (
										<MoreOrderDetails
											order_number={row.order_number}
											books={row.books}
											paymentType={row.payment_type}
											subscription={row.subscription}
											reference={row.reference}
											status={row.status}
											paymentMethod={row.payment_method}
											shippingAddress={row.shipping_address}
											created_at={row.created_at}
										/>
									)}
									header="Order Number"
								/>
								<Column
									body={ReferenceCell}
									header="Reference"
									className="hidden sm:table-cell"
								/>
								<Column
									field="payment_type"
									header="Model"
									className="hidden lg:table-cell"
								/>
								<Column
									header="Payment method"
									body={PaymentMethodCell}
									className="hidden lg:table-cell"
								/>
								<Column
									field="created_at"
									header="Transaction Date"
									className="hidden md:table-cell"
								/>
								<Column
									header="Status"
									body={StatusCell}
									className="hidden md:table-cell"
								/>
								<Column
									header="Amount"
									body={AmountCell}
									className="w-36 text-right"
								/>
							</DataTable>

							{totalRecords > 0 && (
								<div className="flex justify-between items-center">
									<div className="text-sm text-gray-600">
										Showing {(filters.page - 1) * filters.rowsPerPage + 1} to{" "}
										{Math.min(filters.page * filters.rowsPerPage, totalRecords)}{" "}
										of {totalRecords} orders
									</div>
									<CustomPaginator
										setPage={handlePageChange}
										firstPage={(filters.page - 1) * filters.rowsPerPage}
										rowsPerPage={filters.rowsPerPage}
										setFirstPage={handleFirstPageChange}
										setRowsPerPage={handleRowsPerPageChange}
										totalRecords={totalRecords}
										showRowsPerPageDropdown={false}
									/>
								</div>
							)}
						</div>
					</AccountLayout>
				</PaddingX>
			</div>
		</Container>
	);
};

export default MyOrders;
